import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RenewalPipelineChart = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('next90');

  const pipelineData = [
    { 
      period: 'Next 30 Days', 
      value: 485000, 
      probability: 92, 
      accounts: 23,
      risk: 'low'
    },
    { 
      period: '31-60 Days', 
      value: 672000, 
      probability: 85, 
      accounts: 31,
      risk: 'medium'
    },
    { 
      period: '61-90 Days', 
      value: 523000, 
      probability: 78, 
      accounts: 28,
      risk: 'medium'
    },
    { 
      period: '91-120 Days', 
      value: 394000, 
      probability: 65, 
      accounts: 19,
      risk: 'high'
    }
  ];

  const timeframeOptions = [
    { value: 'next90', label: 'Next 90 Days' },
    { value: 'next120', label: 'Next 120 Days' },
    { value: 'next180', label: 'Next 180 Days' }
  ];

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'low': return '#10b981';
      case 'medium': return '#f59e0b';
      case 'high': return '#dc2626';
      default: return '#94a3b8';
    }
  };

  const getRiskIcon = (risk) => {
    switch (risk) {
      case 'low': return 'CheckCircle';
      case 'medium': return 'AlertTriangle';
      case 'high': return 'AlertCircle';
      default: return 'Circle';
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-popover border border-border rounded-lg p-4 shadow-elevation-3">
          <p className="text-text-primary font-medium mb-2">{label}</p>
          <div className="space-y-1">
            <p className="text-text-secondary text-sm">
              Value: <span className="text-text-primary font-medium">${data.value.toLocaleString()}</span>
            </p>
            <p className="text-text-secondary text-sm">
              Probability: <span className="text-text-primary font-medium">{data.probability}%</span>
            </p>
            <p className="text-text-secondary text-sm">
              Accounts: <span className="text-text-primary font-medium">{data.accounts}</span>
            </p>
            <div className="flex items-center space-x-2 mt-2">
              <Icon name={getRiskIcon(data.risk)} size={14} style={{ color: getRiskColor(data.risk) }} />
              <span className="text-sm capitalize" style={{ color: getRiskColor(data.risk) }}>
                {data.risk} Risk
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const totalPipelineValue = pipelineData.reduce((sum, item) => sum + item.value, 0);
  const weightedValue = pipelineData.reduce((sum, item) => sum + (item.value * item.probability / 100), 0);

  return (
    <div className="bg-card border border-border rounded-lg p-6 dashboard-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-text-primary mb-1">Renewal Pipeline</h3>
          <p className="text-text-secondary text-sm">Upcoming renewals by probability</p>
        </div>
        
        <div className="flex bg-muted rounded-lg p-1">
          {timeframeOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => setSelectedTimeframe(option.value)}
              className={`px-3 py-1 text-sm font-medium rounded-md transition-all duration-150 ${
                selectedTimeframe === option.value
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="h-64 mb-6">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={pipelineData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="period" 
              stroke="var(--color-text-secondary)"
              fontSize={12}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis 
              stroke="var(--color-text-secondary)"
              fontSize={12}
              tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar 
              dataKey="value" 
              fill="var(--color-primary)"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="space-y-3 mb-6">
        {pipelineData.map((item, index) => (
          <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center space-x-3">
              <Icon 
                name={getRiskIcon(item.risk)} 
                size={16} 
                style={{ color: getRiskColor(item.risk) }}
              />
              <div>
                <p className="text-text-primary font-medium text-sm">{item.period}</p>
                <p className="text-text-secondary text-xs">{item.accounts} accounts</p>
              </div>
            </div>
            
            <div className="text-right">
              <p className="text-text-primary font-medium text-sm">
                ${item.value.toLocaleString()}
              </p>
              <div className="flex items-center space-x-2">
                <div className="w-16 bg-border rounded-full h-2">
                  <div 
                    className="h-2 rounded-full transition-all duration-300"
                    style={{ 
                      width: `${item.probability}%`,
                      backgroundColor: getRiskColor(item.risk)
                    }}
                  />
                </div>
                <span className="text-text-secondary text-xs">{item.probability}%</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="pt-4 border-t border-border">
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center">
            <p className="text-text-secondary text-sm mb-1">Total Pipeline</p>
            <p className="text-text-primary font-bold text-lg">
              ${totalPipelineValue.toLocaleString()}
            </p>
          </div>
          <div className="text-center">
            <p className="text-text-secondary text-sm mb-1">Weighted Value</p>
            <p className="text-text-primary font-bold text-lg">
              ${Math.round(weightedValue).toLocaleString()}
            </p>
          </div>
        </div>
        
        <div className="flex justify-center mt-4">
          <Button variant="outline" size="sm" iconName="FileText" iconPosition="left">
            Generate Report
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RenewalPipelineChart;